import { useAuth } from '../hooks/useAuth';
import { Navigate } from 'react-router-dom';

// ── Role hierarchy ────────────────────────────────────────────────────────────
export const ROLE_LEVELS = {
  staff:       1,
  nurse:       2,
  coordinator: 3,
  manager:     4,
  admin:       5,
};

// ── Default module access by role ─────────────────────────────────────────────
// This mirrors the permissions table seed data.
// At runtime the `permissions` table (fetched in useAuth.js as
// `permissionDefaults`) is the live source of truth for role defaults. This
// object is only the last-resort fallback for the brief window before that
// table has loaded, or if the fetch fails.
export const DEFAULT_PERMISSIONS = {
  admin:       { hh: true, dme: true, finance: true,  hr: true,  quality: true  },
  manager:     { hh: true, dme: true, finance: true,  hr: true,  quality: true  },
  coordinator: { hh: true, dme: true, finance: false, hr: true,  quality: true  },
  nurse:       { hh: true, dme: true, finance: false, hr: false, quality: false },
  staff:       { hh: true, dme: true, finance: false, hr: false, quality: false },
};

// ── useModules hook — module access ───────────────────────────────────────────
// Priority order:
//   1. profile.modules (per-user override array, e.g. ['hh','dme'])
//   2. permissionDefaults (live from the `permissions` table, loaded in useAuth)
//   3. DEFAULT_PERMISSIONS by role (static last-resort fallback)
// This is the single access-check function — both nav visibility (Sidebar,
// MobileMenu) and route gating (RoleGuard's `module` prop below) call it, so
// they can never disagree with each other.
export const useModules = () => {
  const { profile, permissionDefaults } = useAuth();
  const role = profile?.role || 'staff';
  const userModules = profile?.modules;

  const getAccess = (mod) => {
    if (role === 'admin') return true;
    if (userModules !== null && userModules !== undefined)
      return userModules.includes(mod);
    return permissionDefaults?.[role]?.[mod]?.can_view ?? DEFAULT_PERMISSIONS[role]?.[mod] ?? false;
  };

  return {
    getAccess,
    hasHH:      getAccess('hh'),
    hasDME:     getAccess('dme'),
    hasFinance: getAccess('finance'),
    hasHR:      getAccess('hr'),
    hasQuality: getAccess('quality'),
  };
};

// ── usePermission hook — granular per-module action checks ───────────────────
// View access is governed by useModules()/getAccess() above (and, for a user
// with an individual override, by their profile.modules array). Edit/Delete/
// Export are finer-grained and only exist in the `permissions` table — there
// is no per-user override for them, so they always come from the signed-in
// user's role default for that module, regardless of any view-level override.
export const usePermission = (module) => {
  const { profile, permissionDefaults } = useAuth();
  const { getAccess } = useModules();
  const role = profile?.role || 'staff';

  if (role === 'admin') return { canView: true, canEdit: true, canDelete: true, canExport: true };

  const canView = getAccess(module);
  const row = permissionDefaults?.[role]?.[module];
  return {
    canView,
    canEdit:   canView && (row?.can_edit   ?? true),
    canDelete: canView && (row?.can_delete ?? true),
    canExport: canView && (row?.can_export ?? true),
  };
};

// ── RoleGuard component ───────────────────────────────────────────────────────
// minRole: role-hierarchy floor (e.g. "manager")
// module:  business module the user must have access to (e.g. "finance"),
//          checked via the same getAccess() logic that drives nav visibility
// Both may be given together — the route requires both to pass.
export default function RoleGuard({ children, minRole = 'staff', module, fallback = '/dashboard' }) {
  const { profile, loading } = useAuth();
  const { getAccess } = useModules();
  if (loading) return null;
  const userLevel    = ROLE_LEVELS[profile?.role] || 1;
  const requiredLevel = ROLE_LEVELS[minRole] || 1;
  if (userLevel < requiredLevel) return <Navigate to={fallback} replace />;
  if (module && !getAccess(module)) return <Navigate to={fallback} replace />;
  return children;
}

// ── useRole hook — role checks ────────────────────────────────────────────────
export const useRole = () => {
  const { profile } = useAuth();
  const role = profile?.role || 'staff';
  const level = ROLE_LEVELS[role] || 1;
  return {
    role,
    level,
    isAdmin:       role === 'admin',
    isManager:     level >= ROLE_LEVELS.manager,   // admin + manager
    isCoordinator: level >= ROLE_LEVELS.coordinator, // admin + manager + coordinator
    isNurse:       level >= ROLE_LEVELS.nurse,
    isClinical:    level >= ROLE_LEVELS.nurse,     // nurse, coordinator, manager, admin
    canEdit:       level >= ROLE_LEVELS.coordinator,
    canDelete:     level >= ROLE_LEVELS.manager,
    canExport:     level >= ROLE_LEVELS.manager,
    canManageStaff: role === 'admin',
    canViewFinance: level >= ROLE_LEVELS.manager,
  };
};
